import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconCheck, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { ApiError } from '../api/client';
import { counterpartiesApi } from '../api/counterparties';
import {
  incomingUpdsApi,
  INCOMING_STATUS_COLORS,
  INCOMING_STATUS_LABELS,
  type IncomingUpdInput,
} from '../api/incoming-upd';
import { nomenclatureApi } from '../api/nomenclature';
import { organizationsApi } from '../api/organizations';
import {
  emptyIncomingUpdLineRow,
  IncomingUpdLinesEditor,
  type IncomingUpdLineRow,
} from '../components/IncomingUpdLinesEditor';
import { useAuth } from '../auth/AuthContext';

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function todayInput(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

export function IncomingUpdFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const canDecide = hasRole('ADMIN', 'ACCOUNTANT');

  const [organizationId, setOrganizationId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(isNew ? todayInput() : '');
  const [comment, setComment] = useState('');
  const [lines, setLines] = useState<IncomingUpdLineRow[]>([emptyIncomingUpdLineRow()]);
  const [formError, setFormError] = useState<string[] | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const loadedFor = useRef<string | null>(null);

  const incomingUpdQuery = useQuery({
    queryKey: ['incoming-upd', id],
    queryFn: () => incomingUpdsApi.get(id!),
    enabled: !!id && !isNew,
  });

  const organizations = useQuery({ queryKey: ['organizations'], queryFn: () => organizationsApi.list() });
  const counterparties = useQuery({
    queryKey: ['counterparties', 'all'],
    queryFn: () => counterpartiesApi.list({ pageSize: 500 }),
  });
  const nomenclature = useQuery({
    queryKey: ['nomenclature', 'all'],
    queryFn: () => nomenclatureApi.list({ pageSize: 500 }),
  });

  const incomingUpd = incomingUpdQuery.data?.incomingUpd ?? null;
  const canEditDocument = canEditGlobally && (isNew || incomingUpd?.status === 'NEW');

  useEffect(() => {
    if (!isNew || organizationId) return;
    const items = organizations.data?.items ?? [];
    if (items.length === 1) setOrganizationId(items[0]!.id);
  }, [isNew, organizationId, organizations.data]);

  // Заполняем форму данными документа один раз при загрузке — иначе фоновый
  // рефетч перезаписывал бы то, что пользователь уже успел набрать.
  useEffect(() => {
    if (!incomingUpd || loadedFor.current === incomingUpd.id) return;
    loadedFor.current = incomingUpd.id;

    setOrganizationId(incomingUpd.organizationId);
    setCounterpartyId(incomingUpd.counterpartyId);
    setNumber(incomingUpd.number);
    setDate(toDateInput(incomingUpd.date));
    setComment(incomingUpd.comment ?? '');
    setLines(
      incomingUpd.lines.map((l) => ({
        key: l.id,
        nomenclatureId: l.nomenclatureId,
        name: l.name,
        unitSymbol: l.unitSymbol,
        quantity: l.quantity,
        price: l.price,
        vatRateValue: l.vatRateValue ?? '',
      })),
    );
  }, [incomingUpd]);

  const organizationOptions = (organizations.data?.items ?? []).map((o) => ({ value: o.id, label: o.shortName }));
  const counterpartyOptions = (counterparties.data?.items ?? []).map((c) => ({ value: c.id, label: c.shortName }));

  function buildPayload(): IncomingUpdInput {
    return {
      organizationId,
      counterpartyId,
      number: number.trim(),
      date,
      comment: comment || null,
      lines: lines.map((l) => ({
        nomenclatureId: l.nomenclatureId,
        name: l.name.trim(),
        unitSymbol: l.unitSymbol.trim(),
        quantity: l.quantity,
        price: l.price,
        vatRateValue: l.vatRateValue || null,
      })),
    };
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      return isNew ? incomingUpdsApi.create(payload) : incomingUpdsApi.update(id!, payload);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['incoming-upds'] });
      notifications.show({ color: 'green', message: 'Входящий УПД сохранён' });
      if (isNew) {
        navigate(`/incoming-upd/${result.incomingUpd.id}`, { replace: true });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['incoming-upd', id] });
      }
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (!organizationId) errors.push('Укажите организацию-получателя');
    if (!counterpartyId) errors.push('Укажите поставщика');
    if (!number.trim()) errors.push('Укажите номер документа поставщика');
    if (!date) errors.push('Укажите дату документа');
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.unitSymbol.trim()) errors.push(`Строка ${i + 1}: укажите единицу измерения`);
      if (!l.quantity.trim()) errors.push(`Строка ${i + 1}: укажите количество`);
      if (!l.price.trim()) errors.push(`Строка ${i + 1}: укажите цену`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate();
  }

  const removeMutation = useMutation({
    mutationFn: () => incomingUpdsApi.remove(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incoming-upds'] });
      notifications.show({ color: 'green', message: 'Входящий УПД удалён' });
      navigate('/upd');
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось удалить' });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: () => incomingUpdsApi.accept(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incoming-upd', id] });
      void queryClient.invalidateQueries({ queryKey: ['incoming-upds'] });
      notifications.show({ color: 'green', message: 'Документ подписан' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось подписать' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => incomingUpdsApi.reject(id!, rejectReason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incoming-upd', id] });
      void queryClient.invalidateQueries({ queryKey: ['incoming-upds'] });
      notifications.show({ color: 'green', message: 'Документ отклонён' });
      setRejectOpen(false);
      setRejectReason('');
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось отклонить' });
    },
  });

  function handleRemove() {
    if (!incomingUpd) return;
    if (!window.confirm(`Удалить входящий УПД № ${incomingUpd.number}? Действие необратимо.`)) return;
    removeMutation.mutate();
  }

  function handleAccept() {
    if (!window.confirm('Подписать документ? Действие нельзя отменить.')) return;
    acceptMutation.mutate();
  }

  if (!isNew && !incomingUpd) {
    return <Text c="dimmed">Загрузка…</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/upd')}>
            К списку
          </Button>
          <Title order={2}>{isNew ? 'Регистрация входящего УПД' : `Входящий УПД № ${incomingUpd!.number}`}</Title>
          {incomingUpd && (
            <Badge color={INCOMING_STATUS_COLORS[incomingUpd.status]} variant="light">
              {INCOMING_STATUS_LABELS[incomingUpd.status]}
            </Badge>
          )}
        </Group>

        {incomingUpd && (
          <Group gap="xs">
            {canDecide && incomingUpd.status === 'NEW' && (
              <>
                <Button leftSection={<IconCheck size={16} />} onClick={handleAccept} loading={acceptMutation.isPending}>
                  Подписать
                </Button>
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconX size={16} />}
                  onClick={() => setRejectOpen(true)}
                >
                  Отказать
                </Button>
              </>
            )}
            {canEditDocument && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={handleRemove}
                loading={removeMutation.isPending}
              >
                Удалить
              </Button>
            )}
          </Group>
        )}
      </Group>

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {incomingUpd && incomingUpd.status !== 'NEW' && (
        <Paper withBorder p="md" radius="md">
          <Stack gap={4}>
            <Text fw={500} size="sm">
              Решение принято: {formatDateTime(incomingUpd.decidedAt)}
            </Text>
            {incomingUpd.status === 'REJECTED' && incomingUpd.rejectReason && (
              <Text size="sm" c="red">
                Причина отказа: {incomingUpd.rejectReason}
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      <Group grow align="flex-start">
        <Select
          label="Организация-получатель"
          placeholder="Выберите организацию"
          data={organizationOptions}
          value={organizationId || null}
          onChange={(v) => setOrganizationId(v ?? '')}
          disabled={!canEditDocument || !isNew}
          required
          searchable
        />
        <Select
          label="Поставщик"
          placeholder="Выберите контрагента"
          data={counterpartyOptions}
          value={counterpartyId || null}
          onChange={(v) => setCounterpartyId(v ?? '')}
          disabled={!canEditDocument}
          required
          searchable
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label="Номер документа поставщика"
          value={number}
          onChange={(e) => setNumber(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Дата документа"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
      </Group>

      <IncomingUpdLinesEditor
        lines={lines}
        onChange={setLines}
        canEdit={!!canEditDocument}
        nomenclatureOptions={nomenclature.data?.items ?? []}
      />

      {incomingUpd && (
        <Text size="sm" c="dimmed">
          Всего: {formatMoney(incomingUpd.total)}
        </Text>
      )}

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            {isNew ? 'Зарегистрировать' : 'Сохранить'}
          </Button>
        </Group>
      )}

      <Modal opened={rejectOpen} onClose={() => setRejectOpen(false)} title="Отказать в подписи">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Укажите причину отказа — она попадёт в аудит-лог и должна быть понятна, если к документу
            вернутся позже.
          </Text>
          <Textarea
            label="Причина отказа"
            placeholder="Например: расхождение по количеству со счётом"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            autosize
            minRows={2}
            required
          />
          <Button
            color="red"
            leftSection={<IconX size={16} />}
            onClick={() => rejectMutation.mutate()}
            loading={rejectMutation.isPending}
            disabled={!rejectReason.trim()}
          >
            Отказать в подписи
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
